import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Car, MapPin, Phone, Mail, User, Building2, CheckCircle, Clock, TrendingUp, Shield, ChevronRight, Loader2, AlertCircle, ExternalLink, MessageSquare, Send, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Footer } from "../components/Footer";

// Use runtime config (config.js) with fallback to env variable
// Use REACT_APP_BACKEND_URL for preview/dev, APP_CONFIG for production (Hostinger)
const envBackend = process.env.REACT_APP_BACKEND_URL;
const API = envBackend ? `${envBackend}/api` : (window.APP_CONFIG?.API_URL || '/api');

const PROVINCES = [
  "Álava", "Albacete", "Alicante", "Almería", "Asturias", "Ávila", "Badajoz", "Barcelona",
  "Burgos", "Cáceres", "Cádiz", "Cantabria", "Castellón", "Ciudad Real", "Córdoba", "Cuenca",
  "Girona", "Granada", "Guadalajara", "Guipúzcoa", "Huelva", "Huesca", "Islas Baleares",
  "Jaén", "La Coruña", "La Rioja", "Las Palmas", "León", "Lleida", "Lugo", "Madrid", "Málaga",
  "Murcia", "Navarra", "Ourense", "Palencia", "Pontevedra", "Salamanca", "Santa Cruz de Tenerife",
  "Segovia", "Sevilla", "Soria", "Tarragona", "Teruel", "Toledo", "Valencia", "Valladolid",
  "Vizcaya", "Zamora", "Zaragoza"
];

export default function LandingPage() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [vehicleData, setVehicleData] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [result, setResult] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: "",
    phone: "",
    email: "",
    province: "",
    buyer_type: "particular",
    privacy_accepted: false
  });
  const [manualVehicle, setManualVehicle] = useState({
    price: "",
    year: "",
    kilometers: "",
    fuel_type: "Gasolina",
    co2_emissions: "",
    power_cv: "",
    price_includes_vat: true
  });
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);
  const [marketplaceVehicles, setMarketplaceVehicles] = useState([]);

  // Wake up Railway backend on page load (prevents cold start delay)
  useState(() => {
    axios.get(`${API}/health`).catch(() => {});
    axios.get(`${API}/vehicles`).then(r => setMarketplaceVehicles(r.data.vehicles?.slice(0, 4) || [])).catch(() => {});
  });

  const LOADING_STEPS = [
    "Conectando con mobile.de...",
    "Obteniendo datos del anuncio...",
    "Analizando precio del vehículo...",
    "Calculando transporte Alemania-España...",
    "Calculando impuesto de matriculación...",
    "Estimando impuestos CO₂...",
    "Calculando ITV y homologación...",
    "Preparando tu presupuesto..."
  ];

  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      setLoadingProgress(0);
      return;
    }
    const stepInterval = setInterval(() => {
      setLoadingStep(prev => prev < LOADING_STEPS.length - 1 ? prev + 1 : prev);
    }, 3500);
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => prev < 92 ? prev + Math.random() * 3 + 1 : prev);
    }, 400);
    return () => { clearInterval(stepInterval); clearInterval(progressInterval); };
  }, [loading]);

  const handleScrape = async () => {
    if (!url.trim()) {
      toast.error("Introduce un enlace de mobile.de");
      return;
    }
    if (!url.includes("mobile.de")) {
      toast.error("Solo se aceptan enlaces de mobile.de");
      return;
    }

    // Extract data from URL first
    const urlSlug = url.split('/').find(part => 
      part.includes('inserat') || 
      ['bmw', 'mercedes', 'audi', 'volkswagen', 'porsche', 'opel', 'ford', 'toyota'].some(m => part.toLowerCase().includes(m))
    );
    const titleFromUrl = urlSlug ? urlSlug.replace(/-/g, ' ').replace('auto inserat', '').trim() : '';
    
    // Extract year from URL if present
    const yearMatch = url.match(/(20[0-2]\d)/);
    const yearFromUrl = yearMatch ? yearMatch[1] : '';

    setLoading(true);
    try {
      // Retry logic for Railway cold starts (502 errors)
      let response;
      let lastError;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          response = await axios.post(`${API}/scrape-vehicle`, { url }, { timeout: 120000 });
          break; // Success, exit retry loop
        } catch (err) {
          lastError = err;
          const status = err?.response?.status;
          // Retry on 502 (Railway cold start) or network error, but not on 404
          if (attempt < 2 && (status === 502 || !err.response)) {
            console.log(`Attempt ${attempt} failed (${status || 'network'}), retrying...`);
            continue;
          }
          throw err;
        }
      }
      const data = response.data.data;
      
      if (data.price > 0 && data.kilometers > 0) {
        // Scraping succeeded - calculate costs and show breakdown immediately
        setLoadingProgress(100);
        setLoadingStep(LOADING_STEPS.length - 1);
        
        // Calculate costs
        const calcRes = await axios.post(`${API}/calculate`, {
          vehicle_data: { ...data, url },
          province: "Madrid"
        }, { timeout: 30000 });
        
        await new Promise(r => setTimeout(r, 500));
        setVehicleData({ ...data, url });
        setResult({ ...calcRes.data, vehicle: data });
        setShowResult(true);
        toast.success("¡Datos del vehículo extraídos correctamente!");
      } else {
        // Scraping failed - show manual form with pre-filled data
        setVehicleData({ ...data, url });
        setManualVehicle(prev => ({
          ...prev,
          year: data.year > 2000 ? data.year.toString() : yearFromUrl,
          fuel_type: data.fuel_type || "Gasolina"
        }));
        setShowManualForm(true);
        toast.info(
          <div>
            <strong>Rellena los datos del anuncio</strong>
            <p className="text-sm mt-1">Copia precio, km, año y CO₂ del anuncio de mobile.de</p>
          </div>,
          { duration: 6000 }
        );
      }
    } catch (error) {
      console.error("Scrape error:", error?.response?.status, error?.message);
      // Always show manual form as fallback
      setManualVehicle(prev => ({
        ...prev,
        year: yearFromUrl
      }));
      setShowManualForm(true);
      
      if (error.response && error.response.status === 404) {
        toast.error(error.response.data.detail || "Este anuncio ya no existe en mobile.de. Prueba con otro enlace.", { duration: 6000 });
      } else {
        toast.info(
          <div>
            <strong>Rellena los datos del anuncio</strong>
            <p className="text-sm mt-1">Copia precio, km, año y CO₂ del anuncio de mobile.de</p>
          </div>,
          { duration: 6000 }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualVehicle.price || !manualVehicle.year) {
      toast.error("El precio y año son obligatorios");
      return;
    }
    
    const vehicle = {
      url: url,
      price: parseFloat(manualVehicle.price),
      year: parseInt(manualVehicle.year),
      kilometers: parseInt(manualVehicle.kilometers) || 0,
      fuel_type: manualVehicle.fuel_type,
      co2_emissions: parseInt(manualVehicle.co2_emissions) || 150,
      power_cv: parseInt(manualVehicle.power_cv) || 0,
      power_kw: Math.round((parseInt(manualVehicle.power_cv) || 0) / 1.36),
      price_includes_vat: manualVehicle.price_includes_vat,
      title: `Vehículo ${manualVehicle.year}`,
      make: "",
      model: "",
      image_url: ""
    };
    
    setLoading(true);
    try {
      const calcRes = await axios.post(`${API}/calculate`, {
        vehicle_data: vehicle,
        province: "Madrid"
      }, { timeout: 30000 });
      
      setVehicleData(vehicle);
      setResult({ ...calcRes.data, vehicle });
      setShowManualForm(false);
      setShowResult(true);
    } catch {
      toast.error("Error al calcular. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async () => {
    if (!leadForm.name || !leadForm.phone || !leadForm.email || !leadForm.province) {
      toast.error("Todos los campos son obligatorios");
      return;
    }
    if (!leadForm.privacy_accepted) {
      toast.error("Debes aceptar la política de privacidad");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        ...leadForm,
        vehicle_data: vehicleData
      });
      
      setResult(response.data.calculated_result);
      setShowLeadForm(false);
      setShowResult(true);
      toast.success("¡Cálculo completado!");
    } catch (error) {
      console.error(error);
      toast.error("Error al procesar tu solicitud");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Rellena todos los campos");
      return;
    }
    setSendingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Mensaje enviado correctamente. Te responderemos pronto.");
      setContactForm({ name: "", email: "", message: "" });
      setShowContact(false);
    } catch {
      toast.error("Error al enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-carbon-pattern">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card border-0 border-b border-[rgba(138,138,141,0.15)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/logo-telobajo-new.png" 
              alt="Te Lo Bajo" 
              className="h-14 sm:h-16 w-auto"
              data-testid="logo-image"
            />
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/importacion" className="flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#E0352B] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-importacion">
              <span className="hidden sm:inline">Importación</span><span className="sm:hidden">Import</span>
            </Link>
            <Link to="/ocasion" className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#006AE0] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-nacionales">
              <span className="hidden sm:inline">Nacionales</span><span className="sm:hidden">Nacional</span>
            </Link>
            <Link to="/coches-disponibles" className="flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2DB84E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-stock">
              <Car className="w-4 h-4" /><span className="hidden sm:inline">Stock</span><span className="sm:hidden">Stock</span>
            </Link>
            <Link to="/blog" className="flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#D4901E] text-white text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-2 rounded-full transition-colors" data-testid="nav-blog">
              <BookOpen className="w-4 h-4" /><span className="hidden sm:inline">Blog</span><span className="sm:hidden">Blog</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
          style={{ backgroundImage: "url('/hero-bg.jpg')" }}
        />
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/50 to-[#0A0A0A]/70" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          <div className="animate-fade-in-up opacity-0 stagger-1">
            <span className="inline-block px-4 py-2 rounded-full bg-[#1A1A1A] border border-[rgba(255,59,48,0.3)] text-[#FF3B30] text-sm font-medium mb-6">
              Vehículos seleccionados desde Alemania
            </span>
          </div>
          
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight animate-fade-in-up opacity-0 stagger-2"
            data-testid="hero-title"
          >
            ESE COCHE QUE TANTO TE GUSTA
            <span className="text-[#FF3B30] block mt-2">TE LO BAJAMOS</span>
          </h2>
          
          <p 
            className="text-base sm:text-lg text-[#8A8A8D] mb-10 max-w-2xl mx-auto animate-fade-in-up opacity-0 stagger-3"
            data-testid="hero-subtitle"
          >
            <strong className="text-white">Pega el enlace de mobile.de</strong> y en segundos te damos el precio final 
            en tu puerta: transporte, impuestos, matriculación... <span className="text-[#FF3B30]">todo incluido.</span>
          </p>

          {/* Calculator Card */}
          <div className="glass-card p-6 sm:p-8 max-w-2xl mx-auto animate-fade-in-up opacity-0 stagger-4" data-testid="calculator-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#FF3B30]/20 flex items-center justify-center">
                <ExternalLink className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <p className="text-[#C4C4C6] text-sm text-left">
                Pega aquí el enlace del coche de <strong className="text-white">mobile.de</strong>
              </p>
            </div>
            
            <div className="space-y-4">
              <Input
                type="url"
                placeholder="https://www.mobile.de/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="glass-input w-full h-14 px-5 text-white placeholder:text-[#8A8A8D] text-base"
                data-testid="url-input"
              />
              
              <Button
                onClick={handleScrape}
                disabled={loading}
                className="btn-red w-full h-14 text-lg rounded-xl"
                data-testid="calculate-button"
              >
                {loading ? null : <TrendingUp className="w-5 h-5 mr-2" />}
                {loading ? null : "BÁJAMELO"}
              </Button>

              {loading && (
                <div className="space-y-3 animate-fade-in" data-testid="loading-progress">
                  <div className="w-full bg-[#1C1C1E] rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                  <p className="text-[#C4C4C6] text-sm text-center transition-opacity duration-300">
                    {LOADING_STEPS[loadingStep]}
                  </p>
                </div>
              )}
              
              <button
                onClick={() => {
                  setShowManualForm(true);
                  setVehicleData({ url: "" });
                }}
                className="text-[#8A8A8D] hover:text-[#FF3B30] text-sm transition-colors duration-200 mt-1"
                data-testid="manual-form-link"
              >
                ¿No tienes enlace? <span className="underline">Calcula manualmente</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12" data-testid="features-title">
            DE ALEMANIA A TU PUERTA, SIN COMPLICACIONES
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Car, title: "Lo buscamos", desc: "Encontramos y verificamos el coche que quieres" },
              { icon: Shield, title: "Lo traemos", desc: "Transporte asegurado puerta a puerta" },
              { icon: CheckCircle, title: "Lo legalizamos", desc: "ITV, homologación y matrícula española" },
              { icon: Clock, title: "En 15-25 días", desc: "Listo para conducir con todo en regla" }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="glass-card p-6 text-center hover:border-[#FF3B30]/30 transition-all duration-300"
                data-testid={`feature-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-[#FF3B30]" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-sm text-[#8A8A8D]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketplace Preview */}
      {marketplaceVehicles.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="marketplace-preview">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">COCHES DISPONIBLES</h3>
              <p className="text-[#8A8A8D] text-sm sm:text-base">Ya importados, revisados y listos para entrega inmediata</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {marketplaceVehicles.map(v => {
                const statusCls = v.status === 'vendido' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : v.status === 'reservado' ? 'bg-[#FF9500]/20 text-[#FF9500]' : v.status === 'en_proceso' ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#34C759]/20 text-[#34C759]';
                const statusText = v.status === 'vendido' ? 'Vendido' : v.status === 'reservado' ? 'Reservado' : v.status === 'en_proceso' ? 'En proceso' : 'Disponible';
                return (
                  <Link to="/coches-disponibles" key={v.id} className="glass-card overflow-hidden hover:border-[#FF3B30]/40 transition-all duration-300 group cursor-pointer">
                    <div className="relative aspect-[16/10] bg-[#111] overflow-hidden">
                      {v.images?.[0] ? (
                        <img src={`${API}/images/${v.images[0]}`} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#636366]"><Car className="w-10 h-10" /></div>
                      )}
                      <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusCls}`}>{statusText}</span>
                    </div>
                    <div className="p-3">
                      <p className="text-white font-bold text-sm truncate">{v.title || `${v.make} ${v.model}`}</p>
                      <p className="text-[#8A8A8D] text-xs mt-1">{v.year} · {v.kilometers?.toLocaleString()} km</p>
                      <p className="text-[#FF3B30] font-bold text-lg mt-2">{v.price?.toLocaleString("es-ES")} €</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Link to="/coches-disponibles">
                <Button className="btn-red h-12 px-8 text-base">
                  <Car className="w-5 h-5 mr-2" />Ver todos los coches
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="trust-title">
            SIN SORPRESAS, PRECIO CERRADO
          </h3>
          <p className="text-[#8A8A8D] text-base sm:text-lg mb-10 max-w-2xl mx-auto">
            Te damos el número final desde el primer minuto. Lo que ves es lo que pagas, punto.
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
            {["Sin costes ocultos", "Precio cerrado", "Garantía incluida", "Soporte personalizado"].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#C4C4C6]">
                <CheckCircle className="w-5 h-5 text-[#34C759]" />
                <span className="text-sm sm:text-base">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Manual Entry Dialog */}
      <Dialog open={showManualForm} onOpenChange={setShowManualForm}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Car className="w-5 h-5 text-[#FF3B30]" />
              Datos del anuncio
            </DialogTitle>
            <DialogDescription className="text-[#8A8A8D]">
              Copia los datos que aparecen en mobile.de
            </DialogDescription>
          </DialogHeader>
          
          {/* Help box */}
          <div className="bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-xl p-3 mb-4">
            <p className="text-[#FF9500] text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>No hemos podido leer el anuncio. Copia precio, año, km y CO₂ del anuncio en mobile.de.</span>
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#C4C4C6] text-sm">Precio (€) *</Label>
                <Input
                  type="number"
                  placeholder="25000"
                  value={manualVehicle.price}
                  onChange={(e) => setManualVehicle({...manualVehicle, price: e.target.value})}
                  className="glass-input mt-1"
                  data-testid="manual-price-input"
                />
              </div>
              <div>
                <Label className="text-[#C4C4C6] text-sm">Año *</Label>
                <Input
                  type="number"
                  placeholder="2020"
                  value={manualVehicle.year}
                  onChange={(e) => setManualVehicle({...manualVehicle, year: e.target.value})}
                  className="glass-input mt-1"
                  data-testid="manual-year-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#C4C4C6] text-sm">Kilómetros</Label>
                <Input
                  type="number"
                  placeholder="50000"
                  value={manualVehicle.kilometers}
                  onChange={(e) => setManualVehicle({...manualVehicle, kilometers: e.target.value})}
                  className="glass-input mt-1"
                  data-testid="manual-km-input"
                />
              </div>
              <div>
                <Label className="text-[#C4C4C6] text-sm">Potencia (CV)</Label>
                <Input
                  type="number"
                  placeholder="150"
                  value={manualVehicle.power_cv}
                  onChange={(e) => setManualVehicle({...manualVehicle, power_cv: e.target.value})}
                  className="glass-input mt-1"
                  data-testid="manual-power-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#C4C4C6] text-sm">Combustible</Label>
                <Select 
                  value={manualVehicle.fuel_type} 
                  onValueChange={(value) => setManualVehicle({...manualVehicle, fuel_type: value})}
                >
                  <SelectTrigger className="glass-input mt-1" data-testid="manual-fuel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[rgba(138,138,141,0.2)]">
                    {["Gasolina", "Diésel", "Híbrido", "Eléctrico", "Gas"].map(fuel => (
                      <SelectItem key={fuel} value={fuel} className="text-white hover:bg-[#252525]">{fuel}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#C4C4C6] text-sm">CO₂ (g/km)</Label>
                <Input
                  type="number"
                  placeholder="150"
                  value={manualVehicle.co2_emissions}
                  onChange={(e) => setManualVehicle({...manualVehicle, co2_emissions: e.target.value})}
                  className="glass-input mt-1"
                  data-testid="manual-co2-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={manualVehicle.price_includes_vat}
                onCheckedChange={(checked) => setManualVehicle({...manualVehicle, price_includes_vat: checked})}
                id="vat-check"
                data-testid="manual-vat-checkbox"
              />
              <Label htmlFor="vat-check" className="text-[#C4C4C6] text-sm cursor-pointer">
                El precio incluye IVA alemán (19%)
              </Label>
            </div>
            
            <Button
              onClick={handleManualSubmit}
              className="btn-red w-full h-12 mt-4"
              data-testid="manual-submit-button"
            >
              <ChevronRight className="w-5 h-5 mr-2" />
              CONTINUAR
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Form Dialog */}
      <Dialog open={showLeadForm} onOpenChange={setShowLeadForm}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              Un paso más y lo tienes
            </DialogTitle>
            <DialogDescription className="text-[#8A8A8D]">
              Déjanos tus datos y te mostramos el precio final al instante
            </DialogDescription>
          </DialogHeader>
          
          {vehicleData && (
            <div className="bg-[#111111] rounded-xl p-4 mb-4 border border-[rgba(138,138,141,0.1)]">
              <p className="text-[#8A8A8D] text-sm mb-2">Vehículo seleccionado:</p>
              <p className="text-white font-medium">{vehicleData.title || `Vehículo ${vehicleData.year}`}</p>
              <p className="text-[#FF3B30] font-bold text-lg">{formatPrice(vehicleData.price)}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#C4C4C6] text-sm">Nombre completo *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D]" />
                <Input
                  placeholder="Tu nombre"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                  className="glass-input pl-10"
                  data-testid="lead-name-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#C4C4C6] text-sm">Teléfono *</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D]" />
                <Input
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  className="glass-input pl-10"
                  data-testid="lead-phone-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#C4C4C6] text-sm">Email *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D]" />
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  className="glass-input pl-10"
                  data-testid="lead-email-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#C4C4C6] text-sm">Provincia *</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D] z-10" />
                <Select 
                  value={leadForm.province} 
                  onValueChange={(value) => setLeadForm({...leadForm, province: value})}
                >
                  <SelectTrigger className="glass-input pl-10" data-testid="lead-province-select">
                    <SelectValue placeholder="Selecciona tu provincia" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[rgba(138,138,141,0.2)] max-h-60">
                    {PROVINCES.map(province => (
                      <SelectItem key={province} value={province} className="text-white hover:bg-[#252525]">
                        {province}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-[#C4C4C6] text-sm">Tipo de comprador *</Label>
              <div className="flex gap-4 mt-2">
                <button
                  onClick={() => setLeadForm({...leadForm, buyer_type: "particular"})}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                    leadForm.buyer_type === "particular" 
                      ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" 
                      : "border-[rgba(138,138,141,0.2)] text-[#8A8A8D] hover:border-[#8A8A8D]"
                  }`}
                  data-testid="buyer-type-particular"
                >
                  <User className="w-4 h-4" />
                  Particular
                </button>
                <button
                  onClick={() => setLeadForm({...leadForm, buyer_type: "empresa"})}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                    leadForm.buyer_type === "empresa" 
                      ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white" 
                      : "border-[rgba(138,138,141,0.2)] text-[#8A8A8D] hover:border-[#8A8A8D]"
                  }`}
                  data-testid="buyer-type-empresa"
                >
                  <Building2 className="w-4 h-4" />
                  Empresa
                </button>
              </div>
            </div>
            
            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                checked={leadForm.privacy_accepted}
                onCheckedChange={(checked) => setLeadForm({...leadForm, privacy_accepted: checked})}
                id="privacy-check"
                data-testid="lead-privacy-checkbox"
              />
              <Label htmlFor="privacy-check" className="text-[#8A8A8D] text-sm cursor-pointer leading-relaxed">
                Acepto la <a href="#" className="text-[#FF3B30] hover:underline">política de privacidad</a> y 
                consiento el tratamiento de mis datos para recibir información comercial.
              </Label>
            </div>
            
            <Button
              onClick={handleLeadSubmit}
              disabled={loading}
              className="btn-red w-full h-12 mt-2"
              data-testid="lead-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              {loading ? "CALCULANDO..." : "VER MI PRECIO FINAL"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result Dialog - Shows breakdown BEFORE lead form */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)]">
          <DialogHeader className="pb-1 sm:pb-2">
            <DialogTitle className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#34C759]" />
              Desglose de importación
            </DialogTitle>
          </DialogHeader>
          
          {result && (
            <div>
              {/* Vehicle Summary - Compact row with thumbnail */}
              {vehicleData && (
                <div className="flex items-center gap-3 bg-[#111111] rounded-lg p-3 mb-4 border border-[rgba(138,138,141,0.1)]">
                  {vehicleData.image_url && (
                    <img 
                      src={vehicleData.image_url} 
                      alt={vehicleData.title || "Vehículo"} 
                      className="w-20 h-14 object-cover rounded-md flex-shrink-0"
                      data-testid="vehicle-image"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm truncate">{vehicleData.title || `Vehículo ${vehicleData.year}`}</p>
                    <p className="text-xs text-[#8A8A8D] mt-0.5">
                      {vehicleData.year} · {vehicleData.kilometers?.toLocaleString()} km · {vehicleData.fuel_type}
                    </p>
                  </div>
                </div>
              )}

              {/* PRECIO FINAL - Prominent green block at top */}
              <div className="border-2 border-[#34C759] rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 bg-[#34C759]/5" data-testid="result-total">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                  <span className="text-xs sm:text-sm font-bold text-white">PRECIO FINAL EN TU PUERTA</span>
                  <span className="text-xl sm:text-2xl text-[#34C759] font-bold">{formatPrice(result.total)}</span>
                </div>
                {result.spain_price > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center justify-between text-xs text-[#8A8A8D]">
                      <span>Unidades similares en España</span>
                      <span className="line-through">{formatPrice(result.spain_price)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#34C759]">
                      <TrendingUp className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Te lo bajamos: {formatPrice(result.savings)} de ahorro</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-1.5 text-[#8A8A8D]">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs">En {result.estimated_days} días con matrícula española</span>
                </div>
              </div>

              {/* CTA - Immediately visible */}
              <Button
                onClick={() => {
                  setShowResult(false);
                  setShowLeadForm(true);
                }}
                className="btn-red w-full h-11 sm:h-12 text-sm sm:text-base mb-3 sm:mb-4"
                data-testid="request-management-button"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                QUIERO QUE ME LO BAJEN
              </Button>
              
              {/* Breakdown - Compact */}
              <div className="space-y-0.5 text-xs sm:text-sm" data-testid="result-breakdown">
                <div className="breakdown-row py-1 sm:py-1.5">
                  <span className="text-[#8A8A8D]">Precio del anuncio</span>
                  <span className="text-white font-medium">{formatPrice(result.breakdown.vehicle_price)}</span>
                </div>
                {result.breakdown.vehicle_price > result.breakdown.negotiated_price && (
                  <div className="breakdown-row py-1 sm:py-1.5 bg-[#34C759]/5 -mx-2 px-2 rounded">
                    <span className="text-[#34C759] text-[11px] sm:text-xs">Dto. negociado telobajo.com</span>
                    <span className="text-[#34C759] font-bold">-{formatPrice(result.breakdown.vehicle_price - result.breakdown.negotiated_price)}</span>
                  </div>
                )}
                <div className="breakdown-row py-1 sm:py-1.5">
                  <span className="text-[#8A8A8D]">Precio de compra</span>
                  <span className="text-white font-bold">{formatPrice(result.breakdown.negotiated_price)}</span>
                </div>
                <div className="breakdown-row py-1 sm:py-1.5">
                  <span className="text-[#8A8A8D]">Costes de importación</span>
                  <span className="text-white font-medium">{formatPrice(
                    (result.breakdown.transport_plates || 0) + 
                    (result.breakdown.inspection_origin || 0) + 
                    (result.breakdown.itv_revision || 0) + 
                    (result.breakdown.gestoria_dgt || 0)
                  )}</span>
                </div>
                <p className="text-[#636366] text-[10px] sm:text-[11px] pl-1">Transporte + placas + gestoría + tasas DGT + ITV</p>
                <div className="breakdown-row py-1 sm:py-1.5">
                  <span className="text-[#8A8A8D]">Imp. Matriculación</span>
                  <span className={`font-medium ${result.co2_exempt ? 'text-[#34C759]' : 'text-white'}`}>
                    {result.co2_exempt ? 'Exento (CO₂ ≤120g)' : formatPrice(result.breakdown.iedmt_co2_tax)}
                  </span>
                </div>
                {!result.co2_exempt && result.breakdown.iedmt_detail && (
                  <p className="text-[#636366] text-[10px] sm:text-[11px] pl-1">
                    V.fiscal {formatPrice(result.breakdown.iedmt_detail.fiscal_value)} × {Math.round(result.breakdown.iedmt_detail.coefficient * 100)}% antig. = {formatPrice(result.breakdown.iedmt_detail.base_imponible)} × {result.breakdown.iedmt_detail.co2_rate}%
                  </p>
                )}
                <div className="breakdown-row py-1 sm:py-1.5">
                  <span className="text-[#8A8A8D]">Servicio Te Lo Bajo</span>
                  <span className="text-white font-medium">{formatPrice(result.breakdown.service_commission)}</span>
                </div>
              </div>
              
              {/* Disclaimer - Compact */}
              <p className="text-[#FF9500] text-xs mt-3 px-1">
                <strong>Ojo:</strong> Estimación orientativa. Te llamamos para confirmar el precio exacto.
              </p>
              
              <Button
                onClick={() => {
                  setShowResult(false);
                  setUrl("");
                  setVehicleData(null);
                  setLeadForm({
                    name: "",
                    phone: "",
                    email: "",
                    province: "",
                    buyer_type: "particular",
                    privacy_accepted: false
                  });
                }}
                variant="outline"
                className="btn-secondary w-full h-10 mt-3 text-sm"
                data-testid="new-calculation-button"
              >
                PROBAR CON OTRO COCHE
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)] sm:max-w-md text-center">
          <div className="py-6 space-y-6">
            <div className="w-20 h-20 rounded-full bg-[#34C759]/15 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-[#34C759]" />
            </div>
            
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-white" data-testid="confirmation-title">
                Gracias por confiar en Te Lo Bajo
              </h3>
              <p className="text-[#C4C4C6] text-base leading-relaxed">
                Hemos recibido tu solicitud. Nuestro equipo va a revisar el anuncio y 
                <strong className="text-white"> te llamamos en menos de 24h</strong> para 
                cuadrar todos los detalles.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[rgba(138,138,141,0.15)] rounded-xl p-4 text-left space-y-2">
              <p className="text-[#8A8A8D] text-sm">Tu contacto:</p>
              <p className="text-white font-medium">{leadForm.name}</p>
              <p className="text-[#C4C4C6] text-sm">{leadForm.phone} &middot; {leadForm.email}</p>
            </div>

            <p className="text-[#8A8A8D] text-sm">
              Ese coche que tanto te gusta, <span className="text-[#FF3B30] font-semibold">te lo bajamos.</span>
            </p>

            <Button
              onClick={() => {
                setShowConfirmation(false);
                setShowResult(false);
                setUrl("");
                setVehicleData(null);
                setLeadForm({ name: "", phone: "", email: "", province: "", buyer_type: "particular", privacy_accepted: false });
              }}
              className="btn-red w-full h-12"
              data-testid="confirmation-close-button"
            >
              CERRAR
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent className="glass-card border-[rgba(138,138,141,0.2)]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#FF3B30]" />
              Contacto
            </DialogTitle>
            <DialogDescription className="text-[#8A8A8D]">
              ¿Tienes dudas sobre la importación? Escríbenos y te respondemos en menos de 24h.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm text-[#C4C4C6] mb-1 block">Nombre</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D]" />
                <Input
                  placeholder="Tu nombre"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  className="glass-input pl-10 text-white"
                  data-testid="contact-name-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#C4C4C6] mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8D]" />
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="glass-input pl-10 text-white"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#C4C4C6] mb-1 block">Mensaje</label>
              <textarea
                placeholder="¿En qué podemos ayudarte?"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                rows={4}
                className="glass-input w-full text-white rounded-lg px-4 py-3 resize-none placeholder:text-[#8A8A8D] bg-[#1C1C1E] border border-[rgba(138,138,141,0.2)] focus:border-[#FF3B30] focus:outline-none"
                data-testid="contact-message-input"
              />
            </div>
            <Button
              onClick={handleContactSubmit}
              disabled={sendingContact}
              className="btn-red w-full h-12 rounded-xl"
              data-testid="contact-submit-button"
            >
              {sendingContact ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sendingContact ? "ENVIANDO..." : "ENVIAR MENSAJE"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
